import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  useGetConflictRulesQuery,
  useUpdateConflictRulesMutation,
} from '~entities/recital-manager/api/conflict-rules';

import { Button } from '~/shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { Input } from '~/shared/shadcn/ui/input';
import { Switch } from '~/shared/shadcn/ui/switch';

const conflictRulesFormSchema = z
  .object({
    useAgeBasedGaps: z.boolean(),
    globalMinimumGap: z.coerce.number().min(0).max(10),
    ageGapUnder8: z.coerce.number().min(0).max(10),
    ageGap8to12: z.coerce.number().min(0).max(10),
    ageGap13Plus: z.coerce.number().min(0).max(10),
    detectSiblingConflicts: z.boolean(),
  })
  .refine(
    (data) => {
      // Only validate age gaps if using age-based rules
      if (!data.useAgeBasedGaps) return true;

      // Younger students should have equal or higher gap requirements
      // Under 8 >= 8-12 >= 13+
      return (
        data.ageGapUnder8 >= data.ageGap8to12 &&
        data.ageGap8to12 >= data.ageGap13Plus
      );
    },
    {
      message:
        'Younger age groups must have equal or higher gap requirements (Under 8 ≥ 8-12 ≥ 13+)',
      path: ['ageGapUnder8'], // Show error on first field
    },
  );

export type ConflictRulesFormValues = z.infer<typeof conflictRulesFormSchema>;

interface EditConflictRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recitalId: string;
}

export const EditConflictRulesDialog = ({
  open,
  onOpenChange,
  recitalId,
}: EditConflictRulesDialogProps) => {
  const { data: conflictRules, isLoading } = useGetConflictRulesQuery(recitalId);
  const { mutateAsync: updateRules, isPending: isUpdating } =
    useUpdateConflictRulesMutation();

  const form = useForm<ConflictRulesFormValues>({
    resolver: zodResolver(conflictRulesFormSchema),
    defaultValues: {
      useAgeBasedGaps: true,
      globalMinimumGap: 2,
      ageGapUnder8: 3,
      ageGap8to12: 2,
      ageGap13Plus: 1,
      detectSiblingConflicts: false,
    },
  });

  // Update form when data loads
  useEffect(() => {
    if (conflictRules?.rules) {
      const rules = conflictRules.rules;

      // Validate rules structure
      if (!rules || typeof rules !== 'object') {
        console.error('Invalid rules structure:', rules);
        toast.error('Failed to load conflict rules - invalid data');
        return;
      }

      // Validate ageBasedGaps array
      if (!Array.isArray(rules.ageBasedGaps) || rules.ageBasedGaps.length === 0) {
        console.warn('Missing or invalid age-based gaps, using defaults');
      }

      // Reset form with validated data and fallback defaults
      form.reset({
        useAgeBasedGaps: rules.useAgeBasedGaps ?? true,
        globalMinimumGap: rules.globalMinimumGap ?? 2,
        ageGapUnder8:
          rules.ageBasedGaps?.find((g) => g.minAge === 0)?.minimumGap ?? 3,
        ageGap8to12:
          rules.ageBasedGaps?.find((g) => g.minAge === 8)?.minimumGap ?? 2,
        ageGap13Plus:
          rules.ageBasedGaps?.find((g) => g.minAge === 13)?.minimumGap ?? 1,
        detectSiblingConflicts: rules.detectSiblingConflicts ?? false,
      });
    }
  }, [conflictRules, form]);

  const handleSave = async (values: ConflictRulesFormValues) => {
    try {
      await updateRules({
        recitalId,
        data: {
          useAgeBasedGaps: values.useAgeBasedGaps,
          globalMinimumGap: values.globalMinimumGap,
          ageBasedGaps: [
            { minAge: 0, maxAge: 7, minimumGap: values.ageGapUnder8 },
            { minAge: 8, maxAge: 12, minimumGap: values.ageGap8to12 },
            { minAge: 13, maxAge: 99, minimumGap: values.ageGap13Plus },
          ],
          detectSiblingConflicts: values.detectSiblingConflicts,
        },
      });

      toast.success('Conflict rules updated successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update conflict rules:', error);
      toast.error('Failed to update conflict rules');
    }
  };

  const useAgeBasedGaps = form.watch('useAgeBasedGaps');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Age-Based Rules</DialogTitle>
          <DialogDescription>
            Configure minimum spacing between{' '}
            <span className="text-primary">costume changes</span>. Gap = number
            of other performances between appearances.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">
              <FormField
                control={form.control}
                name="useAgeBasedGaps"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Use age-based rules
                      </FormLabel>
                      <FormDescription>
                        Different gap requirements based on age groups
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {!useAgeBasedGaps ? (
                <FormField
                  control={form.control}
                  name="globalMinimumGap"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Global minimum gap</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={10}
                          {...field}
                          className="w-32"
                        />
                      </FormControl>
                      <FormDescription>
                        Same gap requirement for all students
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="ageGapUnder8"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Under 8: Minimum gap</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            max={10}
                            {...field}
                            className="w-32"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ageGap8to12"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>8-12: Minimum gap</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            max={10}
                            {...field}
                            className="w-32"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ageGap13Plus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>13+: Minimum gap</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            max={10}
                            {...field}
                            className="w-32"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="detectSiblingConflicts"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Detect sibling conflicts
                      </FormLabel>
                      <FormDescription>
                        Flag when siblings are assigned to different shows
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Rules
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
